import { User } from "../../../models/Person";
import { JourneyType } from "../types/interviewSession";

const ENTREPRENEUR_STATUSES = new Set([
  "Entrepreneur (Business Owner/Self-Employed)",
  "Solopreneur (Freelancer/Consultant)",
]);

const CORPORATE_STATUSES = new Set([
  "Working for an organization",
  "In transition",
  "Unemployed",
  "Retired",
  "Full-time parenting",
]);

const STUDENT_STATUS = "Student";

export function mapOccupationToJourney(
  occupationStatus?: string | null
): JourneyType {
  if (!occupationStatus?.trim()) {
    return "corporate";
  }
  if (occupationStatus === STUDENT_STATUS) {
    return "student";
  }
  if (ENTREPRENEUR_STATUSES.has(occupationStatus)) {
    return "entrepreneur";
  }
  if (CORPORATE_STATUSES.has(occupationStatus)) {
    return "corporate";
  }
  return "corporate";
}

function hasText(value?: string | null): boolean {
  return Boolean(value?.trim());
}

/** Universal fields required before AI intake (main + NYC). */
export function isIntakeProfileComplete(
  profile: User | null | undefined,
  isSurveyForm: boolean
): boolean {
  if (!profile?.id) {
    return false;
  }

  if (!hasText(profile.occupationStatus)) {
    return false;
  }

  if (!hasText(profile.shortDescription) && !hasText(profile.fullBio)) {
    return false;
  }

  if (!profile.isMentor && !hasText(profile.professionalExperience)) {
    return false;
  }

  if (!isSurveyForm) {
    return true;
  }

  const surveyFields = [
    profile.zip_code,
    profile.borough,
    profile.gender,
    profile.age,
    profile.race,
    profile.ethnicity,
    profile.education,
    profile.incomeRange,
    profile.first_goal,
    profile.second_goal,
    profile.third_goal,
    profile.long_term_goal,
    profile.howYouFoundUs,
  ];

  if (!surveyFields.every(hasText)) {
    return false;
  }

  const hasCategories =
    Array.isArray(profile.categories) && profile.categories.length > 0;
  const hasIndustries =
    Array.isArray(profile.industries) && profile.industries.length > 0;

  return hasCategories || hasIndustries;
}

/** Profile text used to enrich skills / goals matching (NYC fields only when present). */
export function buildProfileKeywords(
  profile: User | null | undefined,
  isSurveyForm: boolean
): string[] {
  if (!profile) {
    return [];
  }

  const parts: string[] = [
    profile.shortDescription ?? "",
    profile.fullBio ?? "",
    profile.important_goal ?? "",
    profile.company_name ?? "",
    profile.advisor_company ?? "",
    profile.sub_industry ?? "",
    profile.current_industry ?? "",
  ];

  if (isSurveyForm) {
    parts.push(
      profile.first_goal ?? "",
      profile.second_goal ?? "",
      profile.third_goal ?? "",
      profile.long_term_goal ?? "",
      ...(profile.three_goals ?? []),
      ...(profile.categories ?? []),
      ...(profile.areaInterest ?? []),
      ...(profile.industries ?? [])
    );
  }

  return parts
    .map((p) => p.trim())
    .filter((p) => p.length > 0);
}
